import React, { useState } from 'react';
import { X, Minus, Plus, ShoppingBag, Trash2, ArrowRight } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { motion, AnimatePresence } from 'motion/react';
import CheckoutModal from './CheckoutModal';
import { Link } from 'wouter';

interface CartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CartDrawer({ isOpen, onClose }: CartDrawerProps) {
  const { items, updateQuantity, removeFromCart, totalPrice, totalItems } = useCart();
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false);

  const handleCheckoutClick = () => {
    setIsCheckoutModalOpen(true);
  };

  const handleCheckoutClose = () => {
    setIsCheckoutModalOpen(false);
  };

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 z-50 flex justify-end">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onClose}
              className="fixed inset-0 bg-black/60 backdrop-blur-[2px] cursor-pointer"
            />

            {/* Slideout Panel */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 220 }}
              className="relative w-full max-w-sm sm:max-w-md bg-white h-full z-50 flex flex-col shadow-2xl border-l border-[#E5E0D8]"
            >
              {/* Drawer Header */}
              <div className="flex items-center justify-between p-4 sm:p-5 border-b border-[#E5E0D8] bg-[#FAF7F2]">
                <div className="flex items-center gap-2.5">
                  <ShoppingBag className="w-5 h-5 text-[#D97736]" />
                  <h2 className="text-base font-bold text-[#1F1F1F] uppercase tracking-wider">
                    Shopping Bag ({totalItems})
                  </h2>
                </div>
                <button
                  onClick={onClose}
                  aria-label="Close cart"
                  className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-500 hover:text-black transition-colors"
                >
                  <X className="w-4 h-4 stroke-[2]" />
                </button>
              </div>

              {/* Items List */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {items.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center text-center p-6">
                    <div className="w-16 h-16 bg-[#FAF7F2] rounded-full flex items-center justify-center mb-4 border border-[#E5E0D8]">
                      <ShoppingBag className="w-7 h-7 text-gray-400" />
                    </div>
                    <p className="font-bold text-sm text-[#1F1F1F] mb-1">Your bag is empty</p>
                    <p className="text-xs text-gray-500 mb-6 max-w-xs">Explore our authentic traditional spices and powders.</p>
                    <button
                      onClick={onClose}
                      className="btn-terracotta bg-[#D97736] text-white px-6 py-2.5 rounded text-xs font-bold uppercase tracking-wider shadow-sm"
                    >
                      Start Shopping
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {items.map((item) => (
                      <div
                        key={item.cartItemId}
                        className="flex gap-3 p-3 bg-white border border-[#E5E0D8] rounded-lg shadow-sm"
                      >
                        <div className="w-16 h-16 bg-white rounded p-1 border border-gray-100 flex-shrink-0 flex items-center justify-center">
                          <img
                            src={item.image}
                            alt={item.name}
                            referrerPolicy="no-referrer"
                            className="w-full h-full object-contain"
                          />
                        </div>

                        <div className="flex-1 min-w-0 flex flex-col justify-between">
                          <div>
                            <div className="flex justify-between items-start gap-1">
                              <h3 className="font-bold text-xs sm:text-sm text-[#1F1F1F] leading-tight truncate">
                                {item.name}
                              </h3>
                              <button
                                onClick={() => removeFromCart(item.cartItemId)}
                                className="text-gray-400 hover:text-red-600 p-1 transition-colors"
                                title="Remove from bag"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                            <span className="text-[10px] font-semibold text-gray-500">
                              Size: {item.selectedWeight}
                            </span>
                          </div>

                          <div className="flex items-center justify-between mt-2">
                            {/* Quantity controller */}
                            <div className="flex items-center border border-gray-300 rounded h-7 bg-white">
                              <button
                                onClick={() => updateQuantity(item.cartItemId, item.quantity - 1)}
                                className="w-6 h-full flex items-center justify-center text-gray-600 hover:bg-gray-100"
                              >
                                <Minus className="w-3 h-3" />
                              </button>
                              <span className="w-6 text-center text-xs font-bold text-[#1F1F1F]">
                                {item.quantity}
                              </span>
                              <button
                                onClick={() => updateQuantity(item.cartItemId, item.quantity + 1)}
                                className="w-6 h-full flex items-center justify-center text-gray-600 hover:bg-gray-100"
                              >
                                <Plus className="w-3 h-3" />
                              </button>
                            </div>

                            <span className="text-xs sm:text-sm font-bold text-[#1F1F1F]">
                              Rs. {(item.price * item.quantity).toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Drawer Footer */}
              {items.length > 0 && (
                <div className="p-4 border-t border-[#E5E0D8] bg-[#FAF7F2]">
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-xs font-bold uppercase tracking-wider text-gray-600">Subtotal</span>
                    <span className="text-lg font-extrabold text-[#1F1F1F]">
                      Rs. {totalPrice.toFixed(2)}
                    </span>
                  </div>

                  <p className="text-[10px] text-gray-500 mb-3 text-center">
                    Taxes included &bull; Free standard delivery
                  </p>

                  <button
                    onClick={handleCheckoutClick}
                    className="w-full btn-terracotta bg-[#D97736] hover:bg-[#C85A17] text-white py-3.5 rounded font-bold uppercase tracking-wider text-xs shadow-md active:scale-95 transition-all flex items-center justify-center gap-2"
                  >
                    <span>Proceed to Checkout</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <CheckoutModal isOpen={isCheckoutModalOpen} onClose={handleCheckoutClose} />
    </>
  );
}
